/**
 * VoicemailPrompt — "Leave a video message?" modal shown after a missed call.
 */
import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { appNavigate } from '../../../app/actions';
import { dismissPrompt, recordingStarted } from '../../actions';
import { startVoicemailRecording } from '../../functions';
function getErrorMessage(error) {
    return error instanceof Error ? error.message : 'Failed to start voicemail recording';
}
const OVERLAY_STYLE = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    zIndex: 1500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};
const MODAL_STYLE = {
    background: '#1e1e2e',
    borderRadius: 16,
    padding: '32px',
    maxWidth: 400,
    width: '90%',
    textAlign: 'center',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
};
const ICON_STYLE = {
    fontSize: 40,
    marginBottom: 16
};
const TITLE_STYLE = {
    fontSize: 18,
    fontWeight: 700,
    color: '#e0e0e0',
    marginBottom: 8
};
const SUBTITLE_STYLE = {
    fontSize: 14,
    color: '#888',
    marginBottom: 24
};
const GREETING_LABEL_STYLE = {
    color: '#d7e8ff',
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 8
};
const GREETING_VIDEO_STYLE = {
    background: '#080b12',
    borderRadius: 10,
    marginBottom: 18,
    maxHeight: 210,
    width: '100%'
};
const BUTTON_ROW_STYLE = {
    display: 'flex',
    gap: 12,
    justifyContent: 'center'
};
const PRIMARY_BUTTON_STYLE = {
    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.2s'
};
const SECONDARY_BUTTON_STYLE = {
    background: 'transparent',
    color: '#aaa',
    border: '1px solid #444',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 14,
    cursor: 'pointer'
};
const VoicemailPrompt = () => {
    const dispatch = useDispatch();
    const { isPromptVisible, promptData } = useSelector((state) => state['features/voicemail']);
    const handleLeaveMessage = useCallback(async () => {
        if (!promptData)
            return;
        try {
            const result = await startVoicemailRecording({
                calleeId: promptData.calleeId,
                calleePhone: promptData.calleePhone
            });
            dispatch(recordingStarted({
                calleeId: promptData.calleeId,
                calleeName: promptData.calleeName,
                calleePhone: promptData.calleePhone,
                messageId: result.messageId,
                roomName: result.roomName,
                maxDurationSeconds: result.maxDurationSeconds
            }));
            dispatch(appNavigate(result.roomName, {
                hidePrejoin: true,
                startWithAudioMuted: false,
                startWithVideoMuted: false
            }));
        }
        catch (error) {
            dispatch({ type: 'VOICEMAIL_ERROR', error: getErrorMessage(error) });
        }
    }, [dispatch, promptData]);
    const handleDismiss = useCallback(() => {
        dispatch(dismissPrompt());
    }, [dispatch]);
    if (!isPromptVisible || !promptData) {
        return null;
    }
    return (React.createElement("div", { style: OVERLAY_STYLE },
        React.createElement("div", { style: MODAL_STYLE },
            React.createElement("div", { style: ICON_STYLE }, "\uD83C\uDFA5"),
            React.createElement("div", { style: TITLE_STYLE },
                promptData.calleeName,
                " is not available"),
            promptData.greeting?.playbackUrl ? (React.createElement(React.Fragment, null,
                React.createElement("div", { style: GREETING_LABEL_STYLE }, "VideoMail greeting"),
                React.createElement("video", { controls: true, playsInline: true, src: promptData.greeting.playbackUrl, style: GREETING_VIDEO_STYLE }))) : null,
            React.createElement("div", { style: SUBTITLE_STYLE }, "Would you like to leave a video message?"),
            React.createElement("div", { style: BUTTON_ROW_STYLE },
                React.createElement("button", { onClick: handleLeaveMessage, style: PRIMARY_BUTTON_STYLE, type: 'button' }, "Leave Video Message"),
                React.createElement("button", { onClick: handleDismiss, style: SECONDARY_BUTTON_STYLE, type: 'button' }, "Dismiss")))));
};
export default VoicemailPrompt;
